import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Save, Copy } from 'lucide-react';
import { Button } from '../ui/button';
import { Modal } from '../ui/modal';
import { 
  WorkflowBasicsStep, 
  ApprovalStepsStep, 
  AssignApproversStep, 
  OrderRoutingRulesStep 
} from './steps';
import { CreateWorkflowRequest, CreateWorkflowStepDto, WorkflowMatchingMode, WorkflowTemplateDto } from '../../types';
import { useCreateDivisionWorkflow } from '../../hooks/use-division-workflows';
import { useDivision } from '../../hooks/use-divisions';
import { useWorkflowTemplates } from '../../hooks/use-workflow-templates';

interface WorkflowWizardProps {
  divisionId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (workflowId: string) => void;
}

export interface WorkflowWizardData {
  // Step 1: Workflow Basics
  name: string;
  description: string;
  type: string;
  active: boolean;
  defaultStepTimeoutHours?: number; // Workflow-specific step timeout override
  pauseTimeoutHours?: number; // Workflow-specific pause timeout override
  defaultTimeoutWarningHours?: number; // Workflow-level default warning hours before timeout
  requireExplicitSubmit?: boolean; // When true, enforces draft/Submit Review at every step in this workflow

  // Step 2: Approval Steps
  steps: WorkflowStepData[];

  // Step 3: Assign Approvers (embedded in steps)
  
  // Step 4: Order Routing
  matchingMode: WorkflowMatchingMode;
  routingRules: RoutingRule[];
}

export interface WorkflowStepData {
  id: string; // temporary ID for UI
  step: number;
  stepType: string;
  name: string;
  description: string;
  canEditQuantity: boolean;
  allowQuantityIncrease?: boolean; // When false (and canEditQuantity is true), approvers may only decrease quantities, never increase them
  approvalType: 'any' | 'all' | 'specific-count';
  requiredApprovals?: number;
  approverUserIds: string[];
  approverEmails: string[]; // For inviting new users
  autoEscalation: boolean;
  escalationHours?: number;
  bypassIfNoApprover?: boolean;
  auditBypass?: boolean;
  orderLevelOnly?: boolean;
  requireExplicitSubmit?: boolean; // When true, line decisions at this step are staged as drafts until the reviewer clicks Submit Review
  amountThreshold?: number;
  skipIfApprovedBy?: string[];
}

export interface RoutingRule {
  id: string; // temporary ID for UI
  field: string;
  operator: 'equals' | 'contains' | 'greater-than' | 'less-than' | 'between';
  value: string;
  valueEnd?: string; // for 'between' operator
}

const STEPS = [
  { id: 1, name: 'Workflow Basics', icon: '📋' },
  { id: 2, name: 'Approval Steps', icon: '✓' },
  { id: 3, name: 'Assign Approvers', icon: '👥' },
  { id: 4, name: 'Order Routing', icon: '🎯' }
];

export function WorkflowWizard({ divisionId, isOpen, onClose, onSuccess }: WorkflowWizardProps) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [saveAsDraft, setSaveAsDraft] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [duplicateRuleIds, setDuplicateRuleIds] = useState<Set<string>>(new Set());
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [appliedTemplateId, setAppliedTemplateId] = useState<string | undefined>(undefined);
  const createWorkflow = useCreateDivisionWorkflow(divisionId);

  const { data: templates = [] } = useWorkflowTemplates(divisionId);
  const { data: divisionData } = useDivision(divisionId, !!divisionId);

  const [wizardData, setWizardData] = useState<WorkflowWizardData>({
    name: '',
    description: '',
    type: 'approval',
    active: false,
    steps: [],
    matchingMode: 'none',
    routingRules: []
  });

  const updateWizardData = (updates: Partial<WorkflowWizardData>) => {
    setWizardData(prev => ({ ...prev, ...updates }));
  };

  const applyTemplate = (template: WorkflowTemplateDto) => {
    setWizardData(prev => ({
      ...prev,
      name: prev.name || template.name, // don't overwrite if already typed
      description: prev.description || (template.description ?? ''),
      type: template.type,
      steps: template.stepsTemplate.map(s => ({
        id: `step-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${s.step}`,
        step: s.step,
        stepType: s.stepType,
        name: s.name,
        description: s.description ?? '',
        canEditQuantity: s.canEditQuantity,
        allowQuantityIncrease: s.allowQuantityIncrease ?? false,
        approvalType: s.requiredApprovals > 1 ? 'specific-count' as const : 'any' as const,
        requiredApprovals: s.requiredApprovals,
        approverUserIds: [],
        approverEmails: [],
        autoEscalation: false,
        escalationHours: 24,
        bypassIfNoApprover: s.bypassIfNoApprover,
        auditBypass: s.auditBypass,
        orderLevelOnly: s.orderLevelOnly,
        requireExplicitSubmit: s.requireExplicitSubmit ?? false,
        amountThreshold: s.amountThreshold,
        skipIfApprovedBy: s.skipIfApprovedBy ? [...s.skipIfApprovedBy] : [],
      })),
      matchingMode: template.routingRulesTemplate.length > 0 ? 'routingRules' : prev.matchingMode,
      routingRules: template.routingRulesTemplate.map(r => ({
        id: r.id,
        field: r.field, // keep 'jsonPath' as the field so the dropdown shows "Custom JSONPath"
        operator: r.operator as RoutingRule['operator'],
        value: '', // values are intentionally empty in the template — user fills them in
        // Pre-populate the JSONPath expression when it's a custom path rule
        valueEnd: r.field === 'jsonPath' ? (r.customPath ?? '') : undefined,
      })),
    }));
    setAppliedTemplateId(template.id);
    setShowTemplatePicker(false);
  };

  const canProceedToNext = () => {
    switch (currentStep) {
      case 1:
        return wizardData.name.trim().length > 0;
      case 2:
        return wizardData.steps.length > 0;
      case 3:
        return wizardData.steps.every(step => 
          step.approverUserIds.length > 0 || step.approverEmails.length > 0 || step.bypassIfNoApprover
        );
      case 4:
        return true; // Routing rules are optional
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length && canProceedToNext()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSave = async (isDraft = false) => {
    setSaveAsDraft(isDraft);
    
    // Convert wizard data to API request format
    const request: CreateWorkflowRequest = {
      name: wizardData.name,
      description: wizardData.description,
      type: wizardData.type,
      matchingMode: wizardData.matchingMode,
      matchCriteria: {
        matchIdentifiers: [],
        executionIdentifiers: []
      },
      workflowSteps: wizardData.steps.map((step): CreateWorkflowStepDto => ({
        step: step.step,
        stepType: step.stepType,
        name: step.name,
        description: step.description,
        canEditQuantity: step.canEditQuantity,
        allowQuantityIncrease: step.allowQuantityIncrease ?? false,
        orderLevelOnly: step.orderLevelOnly ?? false,
        requireExplicitSubmit: step.requireExplicitSubmit ?? false,
        approvers: {
          type: 'user_specific',
          userIds: step.approverUserIds,
          inviteEmails: step.approverEmails, // Send emails to backend for user creation
          parallelApproval: step.approvalType === 'all' ? false : true,
          requiredApprovals: step.approvalType === 'specific-count' ? (step.requiredApprovals || 1) : (step.approvalType === 'all' ? step.approverUserIds.length : 1)
        },
        conditions: {
          skipIfApprovedBy: step.skipIfApprovedBy ?? [],
          bypassIfNoApprover: step.bypassIfNoApprover ?? false,
          auditBypass: step.auditBypass ?? false,
          amountThreshold: step.amountThreshold
        }
      })),
      routingRules: wizardData.routingRules.map(rule => ({
        id: rule.id,
        field: rule.field === 'jsonPath' ? (rule.valueEnd || '') : rule.field,
        operator: rule.operator,
        value: rule.value,
        valueEnd: rule.operator === 'between' ? rule.valueEnd : undefined
      })),
      settings: {
        autoEscalation: false,
        escalationHours: 24,
        defaultStepTimeoutHours: wizardData.defaultStepTimeoutHours,
        pauseTimeoutHours: wizardData.pauseTimeoutHours,
        defaultTimeoutWarningHours: wizardData.defaultTimeoutWarningHours,
        requireExplicitSubmit: wizardData.requireExplicitSubmit ?? false,
        notifications: {
          email: true,
          slack: false,
          sms: false
        },
        timeoutAction: 'none'
      },
      templateId: appliedTemplateId
    };

    createWorkflow.mutate(request, {
      onSuccess: (response) => {
        setErrorMessage(null);
        onClose();
        if (onSuccess && response.id) {
          onSuccess(response.id);
        }
      },
      onError: (error: any) => {
        // Extract error message from API response
        let message = 'Unknown error';
        if (error.response?.data) {
          // ASP.NET Core error format
          if (typeof error.response.data === 'string') {
            message = error.response.data;
          } else if (error.response.data.title) {
            message = error.response.data.detail || error.response.data.title;
          } else if (error.response.data.message) {
            message = error.response.data.message;
          } else {
            message = JSON.stringify(error.response.data);
          }
        } else if (error.message) {
          message = error.message;
        }
        setErrorMessage(message);
        
        // Parse error message to find duplicate rules
        if (message.includes('Routing rule') && message.includes('already exists')) {
          const duplicates = new Set<string>();
          // Extract the rule details from error message like: "Routing rule '$.ShipVia equals Will Call' already exists"
          const match = message.match(/Routing rule '([^']+)'/);
          if (match && match[1]) {
            const [field, operator, ...valueParts] = match[1].split(' ');
            const value = valueParts.join(' ');
            
            // Find matching rule(s) in current wizard data
            wizardData.routingRules.forEach(rule => {
              const ruleField = rule.field === 'jsonPath' ? rule.valueEnd : rule.field;
              const ruleValue = rule.value;
              const ruleOperator = rule.operator;
              
              if (ruleField?.toLowerCase().trim() === field?.toLowerCase().trim() &&
                  ruleOperator?.toLowerCase().replace('-', '').trim() === operator?.toLowerCase().replace('-', '').trim() &&
                  ruleValue?.toLowerCase().trim() === value?.toLowerCase().trim()) {
                duplicates.add(rule.id);
              }
            });
          }
          setDuplicateRuleIds(duplicates);
        } else {
          setDuplicateRuleIds(new Set());
        }
        
        // Scroll to top to show error
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            {/* Template picker banner (step 1 only) */}
            {templates.length > 0 && (
              <div className="mb-6 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Copy className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Start from a template</p>
                    <p className="text-xs text-blue-700">Pre-fill routing rules, steps, and attributes.</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowTemplatePicker(true)}>
                  Choose Template
                </Button>
              </div>
            )}
            <WorkflowBasicsStep data={wizardData} onChange={updateWizardData} divisionRequireExplicitSubmit={divisionData?.settings?.requireExplicitSubmit ?? false} />
          </>
        );
      case 2:
        return (
          <ApprovalStepsStep
            data={wizardData}
            onChange={updateWizardData}
            forceRequireExplicitSubmit={(divisionData?.settings?.requireExplicitSubmit ?? false) || (wizardData.requireExplicitSubmit ?? false)}
          />
        );
      case 3:
        return (
          <AssignApproversStep
            divisionId={divisionId}
            data={wizardData}
            onChange={updateWizardData}
          />
        );
      case 4:
        return (
          <OrderRoutingRulesStep
            data={wizardData}
            onChange={updateWizardData}
            duplicateRuleIds={duplicateRuleIds}
          />
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Workflow"
      size="xl"
    >
      <div className="flex flex-col h-[80vh]">
        {/* Progress Indicator */}
        <div className="border-b border-gray-200 dark:border-secondary-700 pb-4 mb-6">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-lg mb-2 ${
                      currentStep === step.id
                        ? 'bg-blue-600 text-white'
                        : currentStep > step.id
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 dark:bg-secondary-700 text-gray-600 dark:text-secondary-400'
                    }`}
                  >
                    {currentStep > step.id ? <Check className="w-5 h-5" /> : step.icon}
                  </div>
                  <span
                    className={`text-xs text-center ${
                      currentStep === step.id
                        ? 'text-blue-600 dark:text-blue-400 font-medium'
                        : currentStep > step.id
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-500 dark:text-secondary-400'
                    }`}
                  >
                    {step.name}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 rounded ${
                      currentStep > step.id ? 'bg-green-500' : 'bg-gray-200 dark:bg-secondary-700'
                    }`}
                    style={{ marginBottom: '2rem' }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Error Message Display */}
        {errorMessage && (
          <div role="alert" className="mb-4 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-900 mb-1">Error Creating Workflow</h3>
                <p className="text-sm text-red-800">
                  {errorMessage.includes('already exists in workflow') ? (
                    <span>
                      {errorMessage.split('already exists in workflow')[0]}
                      <span className="font-semibold">already exists in workflow</span>
                      {errorMessage.split('already exists in workflow')[1]}
                    </span>
                  ) : (
                    errorMessage
                  )}
                </p>
              </div>
              <button
                onClick={() => setErrorMessage(null)}
                className="flex-shrink-0 text-red-600 dark:text-red-400 hover:text-red-800"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto px-1">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-secondary-700 mt-6">
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={onClose}
            >
              Cancel
            </Button>
            {currentStep > 1 && (
              <Button
                variant="outline"
                onClick={handleBack}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            {currentStep === STEPS.length && (
              <Button
                variant="outline"
                onClick={() => handleSave(true)}
                disabled={!canProceedToNext() || createWorkflow.isPending}
              >
                <Save className="w-4 h-4 mr-2" />
                Save as Draft
              </Button>
            )}
            {currentStep < STEPS.length ? (
              <Button
                onClick={handleNext}
                disabled={!canProceedToNext()}
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={() => handleSave(false)}
                disabled={!canProceedToNext() || createWorkflow.isPending}
              >
                <Check className="w-4 h-4 mr-2" />
                Create Workflow
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>

    {/* Template picker modal */}
    {showTemplatePicker && (
      <Modal
        isOpen
        onClose={() => setShowTemplatePicker(false)}
        title="Choose a Workflow Template"
        size="lg"
      >
        <div className="space-y-3">
          {templates.map(template => (
            <button
              key={template.id}
              onClick={() => applyTemplate(template)}
              className="w-full text-left p-4 rounded-lg border border-gray-200 dark:border-secondary-700 hover:border-blue-400 hover:bg-blue-50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{template.name}</p>
                  {template.description && (
                    <p className="text-sm text-gray-500 dark:text-secondary-400 mt-0.5">{template.description}</p>
                  )}
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950 text-blue-700 border border-blue-200 dark:border-blue-800 shrink-0 ml-4">
                  {template.type.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="flex gap-4 mt-2 text-xs text-gray-400 dark:text-secondary-500">
                <span>{template.stepsTemplate.length} step{template.stepsTemplate.length !== 1 ? 's' : ''}</span>
                <span>{template.routingRulesTemplate.length} routing rule{template.routingRulesTemplate.length !== 1 ? 's' : ''}</span>
                <span>{template.orderAttributesTemplate.length} attribute{template.orderAttributesTemplate.length !== 1 ? 's' : ''}</span>
              </div>
            </button>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={() => setShowTemplatePicker(false)}>Cancel</Button>
        </div>
      </Modal>
    )}
    </>
  );
}
